// A league, or one team of it, saved on the device in the open league format
// (DIRECTION A13, OPEN_LEAGUE_FORMAT.md, PLAN-2026-10-18 §13.1). The server
// writes the file; this asks for it and hands it to the browser to save,
// named for the league and its season.

import { OpenLeagueFile } from '@fsp/domain';
import { get, messageOf } from './api';

export type ExportOutcome = { ok: true; name: string } | { ok: false; message: string };

function slug(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'league'
  );
}

function save(file: OpenLeagueFile, name: string): void {
  const blob = new Blob([`${JSON.stringify(file, null, 2)}\n`], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // After the click has had its turn: revoking at once can cancel the save.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** The whole league, or `teamId`'s file of it. */
export async function exportLeague(leagueId: string, teamId?: string): Promise<ExportOutcome> {
  const path =
    teamId === undefined
      ? `/leagues/${encodeURIComponent(leagueId)}/export`
      : `/leagues/${encodeURIComponent(leagueId)}/teams/${encodeURIComponent(teamId)}/export`;
  const reply = await get(path, OpenLeagueFile);
  if (reply.status !== 'ok')
    return { ok: false, message: messageOf(reply, 'That file could not be made. Try again.') };
  const file = reply.data;
  const own = file.team === null ? null : file.league.teams.find((t) => t.key === file.team);
  const name = `${slug(own ? own.name : file.league.name)}-${file.league.season}.open-league.json`;
  save(file, name);
  return { ok: true, name };
}
